import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import './ImageModal.css';

const ImageModal = ({ show, onHide, imageUrl, title = "Certificate" }) => {
  const [imageObjectUrl, setImageObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  // Function to fetch and display image from backend
  const fetchImage = async (url) => {
    if (!url) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let urlToFetch = url;

      // Prefer preview variant if URL points to a full-res certificate
      try {
        const parsed = new URL(url);
        if (parsed.pathname.includes('/uploads/certificates/')) {
          const filename = parsed.pathname.split('/').pop() || '';
          const certificateNumber = filename
            .replace(/^cert-/i, '')
            .replace(/-preview/i, '')
            .replace(/\.(png|jpg|jpeg|webp)$/i, '');
          const origin = `${parsed.protocol}//${parsed.hostname}`;
          // Try preview first
          const candidatePreview = `${origin}/uploads/certificates/cert-${certificateNumber}-preview.webp`;
          urlToFetch = candidatePreview;
        }
      } catch (_) {
        // ignore parsing errors; we'll use the original URL
      }

      console.log('Attempting to fetch certificate image from:', urlToFetch);
      
      const response = await fetch(urlToFetch, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Authorization': localStorage.getItem("access_token") 
            ? `Bearer ${localStorage.getItem("access_token")}` 
            : null,
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Verify it's actually an image
      if (!blob.type.startsWith('image/')) {
        throw new Error(`Received non-image content: ${blob.type}`);
      }
      
      const objectUrl = URL.createObjectURL(blob);
      setImageObjectUrl(objectUrl);
      console.log('Successfully loaded certificate image');
    } catch (err) {
      console.error('Error fetching certificate image:', err);
      setError(`Unable to load certificate image: ${err.message}`);
      
      // Try alternative approach - construct a robust download URL using only origin
      if (url.includes('/uploads/certificates/') && !url.includes('/api/certificates/download/')) {
        let downloadUrl = null;
        try {
          const parsed = new URL(url);
          const origin = `${parsed.protocol}//${parsed.host}`; // avoid path fragments like /health
          const filename = parsed.pathname.split('/').pop() || '';
          // Strip leading prefix and extension; also remove optional -preview suffix
          const certificateNumber = filename
            .replace(/^cert-/i, '')
            .replace(/-preview/i, '')
            .replace(/\.(png|jpg|jpeg|webp)$/i, '');
          downloadUrl = `${origin}/api/certificates/download/${certificateNumber}`;
        } catch (e) {
          // Fallback to previous heuristic if URL constructor fails
          const filename = url.split('/').pop();
          const certificateNumber = filename
            .replace(/^cert-/i, '')
            .replace(/-preview/i, '')
            .replace(/\.(png|jpg|jpeg|webp)$/i, '');
          const baseUrl = url.split('/uploads/')[0];
          downloadUrl = `${baseUrl}/api/certificates/download/${certificateNumber}`;
        }
        
        console.log('Trying alternative download URL:', downloadUrl);
        
        try {
          const altResponse = await fetch(downloadUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'Authorization': localStorage.getItem("access_token") 
                ? `Bearer ${localStorage.getItem("access_token")}` 
                : null,
            }
          });
          
          if (altResponse.ok) {
            const altBlob = await altResponse.blob();
            if (altBlob.type.startsWith('image/')) {
              const altObjectUrl = URL.createObjectURL(altBlob);
              setImageObjectUrl(altObjectUrl);
              setError(null);
              console.log('Successfully loaded certificate image via download URL');
              return;
            }
          }
        } catch (altErr) {
          console.error('Alternative download URL also failed:', altErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && imageUrl) {
      fetchImage(imageUrl);
    }
    
    // Cleanup function to revoke object URL when modal closes
    if (!show && imageObjectUrl) {
      URL.revokeObjectURL(imageObjectUrl);
      setImageObjectUrl(null);
      setZoomed(false);
    }
  }, [show, imageUrl]);

  const handleClose = () => {
    if (imageObjectUrl) {
      URL.revokeObjectURL(imageObjectUrl);
      setImageObjectUrl(null);
    }
    setError(null);
    setZoomed(false);
    onHide();
  };

  const handleDownload = () => {
    if (imageObjectUrl) {
      const link = document.createElement('a');
      link.href = imageObjectUrl;
      link.download = `${title.replace(/\s+/g, '_')}_certificate.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleZoom = () => {
    setZoomed(!zoomed);
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      centered
      className="image-modal"
    >
      <Modal.Header closeButton className="image-modal-header">
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="image-modal-body">
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading certificate...</p>
          </div>
        )}
        
        {error && (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <p>Failed to load certificate image</p>
            <small>{error}</small>
            <button 
              className="btn btn-primary mt-2" 
              onClick={() => fetchImage(imageUrl)}
            >
              Retry
            </button>
          </div>
        )}
        
        {imageObjectUrl && !loading && !error && (
          <div className="image-container">
            <img 
              src={imageObjectUrl} 
              alt={title}
              className={`certificate-image ${zoomed ? 'zoomed' : ''}`}
              onClick={toggleZoom}
              title={zoomed ? "Click to zoom out" : "Click to zoom in"}
            />
            <div className="zoom-hint">
              {zoomed ? "Click image to zoom out" : "Click image to zoom in"}
            </div>
          </div>
        )}
      </Modal.Body>
      
      {imageObjectUrl && !loading && !error && (
        <Modal.Footer className="image-modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={handleClose}
          >
            Close
          </button>
          <button 
            className="btn btn-info" 
            onClick={toggleZoom}
          >
            {zoomed ? "Zoom Out" : "Zoom In"}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleDownload}
          >
            Download Certificate
          </button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ImageModal;
